import { AxiosHttpAuthClient } from '../axios-http-auth-client'
import {
  ApiInstanceRequest,
  ApiInstanceResponse,
  ApiInstanceReturn,
  ErrorFactory
} from './http-service-client-types'
import { HttpStatusCode } from '@/data/protocols/http'
import {
  InvalidCredentialsError,
  ServerError,
  ForbiddenError,
  NotFoundError,
  ConflictError,
  BadRequestError,
  ServiceUnavailableError,
  TooManyRequestsError,
  PreconditionFailedError
} from '@/domain/errors'
import { clearAuthData, redirectToLogin } from '@/data/services/login/login-service-utils'
import errorMonitor from '@/infra/tracking/error-monitor'

const errorMap: Record<number, ErrorFactory> = {
  [HttpStatusCode.badRequest]: msg => new BadRequestError(msg),
  [HttpStatusCode.forbidden]: msg => new ForbiddenError(msg),
  [HttpStatusCode.unauthorized]: msg => new InvalidCredentialsError(msg),
  [HttpStatusCode.notFound]: msg => new NotFoundError(msg),
  [HttpStatusCode.conflict]: msg => new ConflictError(msg),
  [HttpStatusCode.preconditionFailed]: msg => new PreconditionFailedError(msg),
  [HttpStatusCode.tooManyRequests]: msg => new TooManyRequestsError(msg),
  [HttpStatusCode.serverError]: msg => new ServerError(msg),
  [HttpStatusCode.serviceUnavailable]: msg => new ServiceUnavailableError(msg)
}

/**
 * Extrai a mensagem de erro da resposta.
 *
 * Prioriza o envelope padrão (`body.message`), mas cai em `body.data.message`
 * para corpos de erro não-padrão (ex.: `{ error, message, httpCode }`), que o
 * executeRequest reembrulha em `{ success: true, data: <corpo> }` — deixando a
 * mensagem aninhada em `data`.
 */
const extractErrorMessage = <T>(response: ApiInstanceReturn<T>): string | null | undefined => {
  if (response.body?.message) {
    return response.body.message
  }

  const data = response.body?.data
  if (data && typeof data === 'object' && 'message' in data) {
    return (data as { message?: string | null }).message
  }

  return undefined
}

/**
 * Handles errors based on the API response status code and body.
 *
 * - If the response status code matches an entry in `errorMap`, throws the corresponding error with the response message.
 * - If the response status code indicates success but the response body indicates failure, throws a bad request error.
 * - Otherwise, does nothing.
 *
 * @template T The type of the response body.
 * @param response The API response object containing status code and body.
 * @throws Throws an error if the response status code or body indicates a failure.
 */
const handleError = <T>(
  response: ApiInstanceReturn<T>,
  config?: ApiInstanceRequest
): never | void => {
  const errorFactory = errorMap[response.statusCode]
  if (errorFactory) {
    const error = Object.assign(errorFactory(extractErrorMessage(response)), {
      statusCode: response.statusCode,
      correlationId: response.correlationId,
      data: response.body?.data,
      response: {
        data: response.body
      }
    })

    if (response.statusCode === HttpStatusCode.serverError) {
      errorMonitor.captureException(error, {
        tags: {
          http_method: config?.method ?? 'unknown',
          http_status: String(response.statusCode)
        },
        extra: {
          url: config?.url,
          params: config?.params,
          responseBody: response.body
        }
      })
    }

    throw error
  }

  const isSuccessStatus =
    response.statusCode >= HttpStatusCode.ok && response.statusCode < HttpStatusCode.multipleChoices

  // If status is success but body indicates failure, throw BadRequestError
  const bodyIsFailure =
    response.body == null || (typeof response.body === 'object' && response.body.success === false)

  if (isSuccessStatus && bodyIsFailure) {
    throw Object.assign(errorMap[HttpStatusCode.badRequest](extractErrorMessage(response)), {
      statusCode: response.statusCode,
      correlationId: response.correlationId,
      response: {
        data: response.body
      }
    })
  }
}

/**
 * Creates an instance of `AxiosHttpAuthClient` configured to handle authentication redirects.
 *
 * When authentication is required (401 Unauthorized), the client will:
 * 1. Clear all authentication data (cookies, sessionStorage)
 * 2. Redirect to internal login page
 *
 * @param skipAuthRedirect - If true, skip automatic redirect to login on 401 errors
 * @returns {AxiosHttpAuthClient} An authenticated HTTP client for making requests.
 */
const createAuthClient = (skipAuthRedirect = false): AxiosHttpAuthClient => {
  return new AxiosHttpAuthClient(() => {
    console.log('[HttpServiceClient] ❌ 401 Unauthorized, clearing auth and redirecting to login')
    clearAuthData()
    redirectToLogin(window.location.href)
  }, skipAuthRedirect)
}

const inFlightRequests = new Map<string, Promise<ApiInstanceReturn<unknown>>>()

async function executeRequest<T>(config: ApiInstanceRequest): Promise<ApiInstanceReturn<T>> {
  const axiosHttpAuthClient = createAuthClient(config.skipAuthRedirect)

  const apiResponse = await axiosHttpAuthClient.request({
    ...config,
    signal: config.signal,
    responseType: config.responseType
  })

  const isStandardResponse =
    apiResponse.body != null && typeof apiResponse.body === 'object' && 'data' in apiResponse.body

  const body: ApiInstanceResponse<T> = isStandardResponse
    ? (apiResponse.body as ApiInstanceResponse<T>)
    : { success: true, data: apiResponse.body as T }

  const response: ApiInstanceReturn<T> = {
    statusCode: apiResponse.statusCode,
    headers: apiResponse.headers,
    correlationId: apiResponse.correlationId,
    body
  }

  handleError(response, config)

  return response
}

/**
 * Makes an authenticated HTTP request with GET deduplication.
 *
 * GET requests without a signal are deduplicated by URL + params —
 * concurrent identical calls share the same in-flight Promise.
 * Requests with a signal (React Query) or mutations bypass deduplication.
 */
export function httpServiceClient<T>(config: ApiInstanceRequest): Promise<ApiInstanceReturn<T>> {
  const isGet = config.method === 'get'

  if (isGet && !config.signal) {
    const key = `get:${config.url}:${JSON.stringify(config.params ?? {})}`

    const existingKey = inFlightRequests.get(key)
    if (existingKey) {
      return existingKey as Promise<ApiInstanceReturn<T>>
    }

    const promise = executeRequest<T>(config).finally(() => inFlightRequests.delete(key))
    inFlightRequests.set(key, promise)

    return promise
  }

  return executeRequest<T>(config)
}
